import {
  Badge,
  Button,
  Group,
  Modal,
  Select,
  Stack,
  Text,
  TextInput,
  Textarea,
} from "@mantine/core";
import { useState } from "react";
import { useNavigate } from "react-router";
import { semanticColor, type SemanticRole } from "@how/design-system";
import { ConfirmActionModal } from "@how/ui";
import { useDeleteTag, useSaveTag, type TagFields, type TagRow } from "./tag-queries";

/** What a church member calls the colour, mapped onto the role the theme renders it as. */
const COLOURS: readonly { readonly value: SemanticRole; readonly label: string }[] = [
  { value: "neutral", label: "Grey" },
  { value: "brand", label: "Blue" },
  { value: "info", label: "Light blue" },
  { value: "success", label: "Green" },
  { value: "warning", label: "Orange" },
  { value: "danger", label: "Red" },
  { value: "achievement", label: "Gold" },
  { value: "special", label: "Purple" },
];

const EMPTY: TagFields = { name: "", color: "neutral", description: "" };

export interface TagModalProps {
  /** null to create a tag, a row to edit it; undefined when closed. */
  readonly tag: TagRow | null | undefined;
  readonly canManage: boolean;
  readonly onClose: () => void;
}

/** One dialog for creating and editing a tag, so the two cannot drift apart. */
export function TagModal({ tag, canManage, onClose }: TagModalProps) {
  const navigate = useNavigate();
  const save = useSaveTag();
  const remove = useDeleteTag();
  // The page remounts this dialog per tag through its key, so the form starts from the tag being opened without an
  // effect to copy props into state.
  const [fields, setFields] = useState<TagFields>(
    tag === null || tag === undefined
      ? EMPTY
      : { name: tag.name, color: tag.color, description: tag.description ?? "" },
  );
  const [confirming, setConfirming] = useState(false);

  const duplicate =
    save.error !== null && (save.error as { code?: string } | null)?.code === "23505";
  const preview = fields.name.trim().length === 0 ? "Tag preview" : fields.name.trim();

  return (
    <>
      <Modal
        opened={tag !== undefined}
        onClose={onClose}
        title={tag === null ? "Add a tag" : "Edit tag"}
        radius="lg"
      >
        <Stack gap="sm">
          <TextInput
            label="Name"
            required
            disabled={!canManage}
            value={fields.name}
            onChange={(event) => {
              const value = event.currentTarget.value;
              setFields((current) => ({ ...current, name: value }));
            }}
          />
          <Select
            label="Colour"
            description="The theme picks the exact shade, so a tag stays readable in light and dark mode."
            disabled={!canManage}
            data={COLOURS.map((c) => ({ value: c.value, label: c.label }))}
            value={fields.color}
            allowDeselect={false}
            onChange={(value) => {
              const match = COLOURS.find((c) => c.value === value);
              if (match !== undefined) setFields((current) => ({ ...current, color: match.value }));
            }}
          />
          <Textarea
            label="What it means"
            placeholder="For example: first visit in the last three months."
            disabled={!canManage}
            value={fields.description}
            onChange={(event) => {
              const value = event.currentTarget.value;
              setFields((current) => ({ ...current, description: value }));
            }}
            autosize
            minRows={2}
          />
          <Group gap="xs">
            <Text size="sm" c="dimmed">
              Preview
            </Text>
            <Badge variant="light" color={semanticColor(fields.color)}>
              {preview}
            </Badge>
          </Group>

          {duplicate ? (
            <Text size="sm" c="red">
              Another tag already has that name.
            </Text>
          ) : save.isError ? (
            <Text size="sm" c="red">
              The tag was not saved. Check your access and try again.
            </Text>
          ) : null}

          <Group justify="space-between" mt="xs">
            <Group gap="xs">
              {tag !== null && tag !== undefined ? (
                <>
                  <Button
                    variant="subtle"
                    onClick={() => {
                      onClose();
                      void navigate(`/people?tag=${tag.id}`);
                    }}
                  >
                    Show {tag.peopleCount === 1 ? "1 person" : `${String(tag.peopleCount)} people`}
                  </Button>
                  {canManage ? (
                    <Button
                      variant="subtle"
                      color="red"
                      onClick={() => {
                        setConfirming(true);
                      }}
                    >
                      Delete
                    </Button>
                  ) : null}
                </>
              ) : null}
            </Group>
            {canManage ? (
              <Button
                disabled={fields.name.trim().length === 0}
                loading={save.isPending}
                onClick={() => {
                  save.mutate(
                    { tagId: tag?.id ?? null, fields },
                    {
                      onSuccess: () => {
                        onClose();
                      },
                    },
                  );
                }}
              >
                {tag === null ? "Add tag" : "Save tag"}
              </Button>
            ) : null}
          </Group>
        </Stack>
      </Modal>

      <ConfirmActionModal
        opened={confirming}
        onClose={() => {
          setConfirming(false);
        }}
        title="Delete tag"
        message={
          tag === null || tag === undefined
            ? ""
            : tag.peopleCount === 0
              ? `Delete the tag ${tag.name}? Nobody carries it.`
              : `Delete the tag ${tag.name}? It is removed from ${String(tag.peopleCount)} ${tag.peopleCount === 1 ? "person" : "people"}, and their records stay as they are otherwise.`
        }
        confirmLabel="Delete tag"
        danger
        loading={remove.isPending}
        onConfirm={() => {
          if (tag === null || tag === undefined) return;
          remove.mutate(tag.id, {
            onSuccess: () => {
              setConfirming(false);
              onClose();
            },
          });
        }}
      />
    </>
  );
}
